import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  ArrowClockwiseRegular,
  ArrowSwapRegular,
  CheckmarkCircleRegular,
  DocumentRegular,
  ClockRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { getTaxReturns, getSystemUsers, updateTaxReturn } from '../services/api';
import { CardSkeleton } from '../components/LoadingSkeleton';
import type { TaxReturn, SystemUser, TaxReturnStatus } from '../types';
import { statusDisplayNames, statusColors } from '../types';
import { useWorkloadScopeParams } from './useWorkloadScopeParams';

// Helper to safely get status display name
const getStatusDisplayName = (status: string): string => {
  return statusDisplayNames[status as TaxReturnStatus] || status;
};

// Helper to safely get status color
const getStatusColor = (status: string): string => {
  return statusColors[status as TaxReturnStatus] || '#888';
};

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1;

interface PreparerWorkload {
  preparerId: string;
  preparerName: string;
  total: number;
  byStatus: Record<string, number>;
  urgentCount: number; // appointments in next 7 days
  overdueCount: number; // past due appointments
}

export default function WorkloadPage() {
  const queryClient = useQueryClient();
  const { year: selectedYear, setYear: setSelectedYear } = useWorkloadScopeParams();
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedPreparer, setSelectedPreparer] = useState<PreparerWorkload | null>(null);
  const [targetPreparer, setTargetPreparer] = useState<string>('');
  const [statusToReassign, setStatusToReassign] = useState<string>('all');

  // Fetch all tax returns
  const { data: taxReturns, isLoading: returnsLoading, refetch } = useQuery<TaxReturn[]>({
    queryKey: ['taxReturns', selectedYear],
    queryFn: () => getTaxReturns({ taxYear: selectedYear }),
  });

  // Fetch all system users
  const { data: users, isLoading: usersLoading } = useQuery<SystemUser[]>({
    queryKey: ['systemUsers'],
    queryFn: getSystemUsers,
  });

  // Reassign mutation
  const reassignMutation = useMutation({
    mutationFn: async ({ returnIds, newPreparerId, newPreparerName }: {
      returnIds: string[];
      newPreparerId: string;
      newPreparerName: string;
    }) => {
      for (const id of returnIds) {
        await updateTaxReturn(id, {
          preparerId: newPreparerId,
          preparerName: newPreparerName,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      setReassignDialogOpen(false);
      setSelectedPreparer(null);
      setTargetPreparer('');
      setStatusToReassign('all');
    },
  });

  // Calculate workload per preparer
  const workloadData = useMemo(() => {
    if (!taxReturns) return [];

    const now = new Date();
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const preparerMap = new Map<string, PreparerWorkload>();

    // Initialize with "Unassigned"
    preparerMap.set('', {
      preparerId: '',
      preparerName: 'Unassigned',
      total: 0,
      byStatus: {},
      urgentCount: 0,
      overdueCount: 0,
    });

    taxReturns.forEach((tr) => {
      const preparerId = tr.preparerId || '';
      const preparerName = tr.preparerName || 'Unassigned';

      if (!preparerMap.has(preparerId)) {
        preparerMap.set(preparerId, {
          preparerId,
          preparerName,
          total: 0,
          byStatus: {},
          urgentCount: 0,
          overdueCount: 0,
        });
      }

      const workload = preparerMap.get(preparerId)!;
      workload.total++;
      workload.byStatus[tr.status] = (workload.byStatus[tr.status] || 0) + 1;

      // Check urgency
      if (tr.scheduledDate) {
        const scheduled = new Date(tr.scheduledDate);
        if (scheduled < now && tr.status !== 'completed') {
          workload.overdueCount++;
        } else if (scheduled >= now && scheduled <= nextWeek) {
          workload.urgentCount++;
        }
      }
    });

    return Array.from(preparerMap.values())
      .filter((w) => w.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [taxReturns]);

  // Get returns to reassign
  const returnsToReassign = useMemo(() => {
    if (!selectedPreparer || !taxReturns) return [];

    return taxReturns.filter((tr) => {
      const matches = (tr.preparerId || '') === selectedPreparer.preparerId;
      if (!matches) return false;
      if (statusToReassign === 'all') return true;
      return tr.status === statusToReassign;
    });
  }, [selectedPreparer, taxReturns, statusToReassign]);

  // Get target preparer name
  const targetPreparerName = users?.find((u) => u.systemUserId === targetPreparer)?.fullName || '';

  const handleReassign = () => {
    if (!targetPreparer || returnsToReassign.length === 0) return;

    reassignMutation.mutate({
      returnIds: returnsToReassign.map((r) => r.id),
      newPreparerId: targetPreparer,
      newPreparerName: targetPreparerName,
    });
  };

  // Calculate totals
  const totals = useMemo(() => {
    return workloadData.reduce(
      (acc, w) => ({
        total: acc.total + w.total,
        urgent: acc.urgent + w.urgentCount,
        overdue: acc.overdue + w.overdueCount,
      }),
      { total: 0, urgent: 0, overdue: 0 }
    );
  }, [workloadData]);

  if (returnsLoading || usersLoading) {
    return <CardSkeleton />;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Preparer Workload</h2>
        <p>View and manage preparer assignments and workload distribution</p>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#0078d4' }}>{totals.total}</span>
          <span className="stat-label">Total Returns</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#107c10' }}>{workloadData.length}</span>
          <span className="stat-label">Active Preparers</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#ca5010' }}>{totals.urgent}</span>
          <span className="stat-label">Due This Week</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#a4262c' }}>{totals.overdue}</span>
          <span className="stat-label">Past Due</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="filter-group">
          <Dropdown
            placeholder="Tax Year"
            value={selectedYear.toString()}
            onOptionSelect={(_, data) => setSelectedYear(Number(data.optionValue))}
            style={{ minWidth: '120px' }}
          >
            {[defaultTaxYear, defaultTaxYear - 1, defaultTaxYear - 2].map((year) => (
              <Option key={year} text={year.toString()} value={year.toString()}>
                {year}
              </Option>
            ))}
          </Dropdown>
        </div>

        <Button
          appearance="subtle"
          icon={<ArrowClockwiseRegular />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </div>

      {/* Workload Table */}
      <div className="card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '200px' }}>Preparer</TableHeaderCell>
              <TableHeaderCell style={{ width: '100px' }}>Total</TableHeaderCell>
              <TableHeaderCell>Status Distribution</TableHeaderCell>
              <TableHeaderCell style={{ width: '100px' }}>Urgent</TableHeaderCell>
              <TableHeaderCell style={{ width: '100px' }}>Overdue</TableHeaderCell>
              <TableHeaderCell style={{ width: '120px' }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workloadData.map((workload) => (
              <TableRow key={workload.preparerId || 'unassigned'}>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PersonRegular />
                    <span style={{ fontWeight: 500 }}>
                      {workload.preparerName}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge appearance="filled" color="brand" size="large">
                    {workload.total}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {Object.entries(workload.byStatus).map(([status, count]) => (
                      <Tooltip key={status} content={getStatusDisplayName(status)} relationship="label">
                        <Badge
                          size="small"
                          style={{
                            backgroundColor: getStatusColor(status),
                            color: 'white',
                          }}
                        >
                          {count}
                        </Badge>
                      </Tooltip>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {workload.urgentCount > 0 ? (
                    <Badge appearance="filled" color="warning" icon={<ClockRegular />}>
                      {workload.urgentCount}
                    </Badge>
                  ) : (
                    <span style={{ color: 'var(--tl-text-tertiary)' }}>-</span>
                  )}
                </TableCell>
                <TableCell>
                  {workload.overdueCount > 0 ? (
                    <Badge appearance="filled" color="danger" icon={<WarningRegular />}>
                      {workload.overdueCount}
                    </Badge>
                  ) : (
                    <CheckmarkCircleRegular style={{ color: '#107c10' }} />
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ArrowSwapRegular />}
                    onClick={() => {
                      setSelectedPreparer(workload);
                      setReassignDialogOpen(true);
                    }}
                  >
                    Reassign
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {workloadData.length === 0 && (
          <div className="empty-state">
            <DocumentRegular style={{ fontSize: 48, color: 'var(--tl-text-tertiary)' }} />
            <p>No tax returns found for {selectedYear}.</p>
          </div>
        )}
      </div>

      {/* Status Legend */}
      <div className="card" style={{ marginTop: '16px' }}>
        <h4 style={{ margin: '0 0 12px 0' }}>Status Legend</h4>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {Object.entries(statusDisplayNames).map(([status, name]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: getStatusColor(status),
                }}
              />
              <span style={{ fontSize: tokens.fontSizeBase200 }}>{name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={(_, data) => setReassignDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              Reassign Returns from {selectedPreparer?.preparerName}
            </DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                    Which returns to reassign?
                  </label>
                  <Dropdown
                    value={statusToReassign}
                    onOptionSelect={(_, data) => setStatusToReassign(data.optionValue || 'all')}
                    style={{ width: '100%' }}
                  >
                    <Option text={`All Returns (${selectedPreparer?.total || 0})`} value="all">
                      All Returns ({selectedPreparer?.total || 0})
                    </Option>
                    {selectedPreparer && Object.entries(selectedPreparer.byStatus).map(([status, count]) => (
                      <Option key={status} text={`${getStatusDisplayName(status)} (${count})`} value={status}>
                        {getStatusDisplayName(status)} ({count})
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                    Reassign to:
                  </label>
                  <Dropdown
                    placeholder="Select preparer"
                    value={targetPreparer}
                    onOptionSelect={(_, data) => setTargetPreparer(data.optionValue || '')}
                    style={{ width: '100%' }}
                  >
                    {users?.filter((u) => u.systemUserId !== selectedPreparer?.preparerId).map((user) => (
                      <Option key={user.systemUserId} value={user.systemUserId}>
                        {user.fullName}
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                {targetPreparer && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f3f2f1',
                    borderRadius: '4px',
                  }}>
                    <strong>{returnsToReassign.length}</strong> returns will be reassigned
                    from <strong>{selectedPreparer?.preparerName}</strong> to{' '}
                    <strong>{targetPreparerName}</strong>
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setReassignDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleReassign}
                disabled={!targetPreparer || returnsToReassign.length === 0 || reassignMutation.isPending}
              >
                {reassignMutation.isPending ? 'Reassigning...' : `Reassign ${returnsToReassign.length} Returns`}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
